import { useTranslation } from "react-i18next";
import Co2EmissionFixed from "../CO2EmissionFixed";
import { AccordionItem } from "../../../AccordianItem";
import {
  Checkbox,
  FileInput,
  Grid,
  Select,
  Switch,
  Text,
  TextInput,
  useMantineColorScheme,
} from "@mantine/core";
import { IconCaretDownFilled, IconFileSearch, IconPercentage } from "@tabler/icons-react";
import { addedStepsAtom } from "../../../../utils/atoms/addedStepsAtom";
import { useAtom } from "jotai";
import PropTypes from "prop-types";
import { useHelpFocusBlur } from "../../../../utils/useHelpFocusBlur";
import { helpAtom } from "../../../../utils/atoms/helpAtom";
import { useMemo, useRef } from "react";
import { responsiveCol } from "../../../../constants/helper";

const GridPowerExchange = ({
  form,
  handleInputChange,
  gridParameters,
  getFieldAttributes,
}) => {
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();

  const grayIconStyle = useMemo(
    () => ({
      padding: "4px",
    }),
    [colorScheme],
  );
  const [addedStepsSeries] = useAtom(addedStepsAtom);
  const containerRef = useRef(null);
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);

  useHelpFocusBlur(containerRef, setHelperAtom, helperAtom);
  const handleContainerClick = (event) => {
    const interactiveTags = ["INPUT", "SELECT", "TEXTAREA", "BUTTON", "LABEL"];
    if (interactiveTags.includes(event.target.tagName)) return;
    const cardElement = document.querySelector('[card-id="accordion-grid-1"]');
    if (cardElement) {
      cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
      cardElement.style.transition = "background-color 0.5s ease";
      cardElement.style.border = "2px solid #42b4e6";
      setTimeout(() => {
        cardElement.style.border = "";
      }, 2000);
    }
  };

  const handleContainerKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleContainerClick(event);
    }
  };

  const header = () => (
    <>
      {" "}
      <span className="stepText col-md-1 px-0">
        {`Step ${addedStepsSeries.indexOf("grid") + 1}`}: A.
      </span>
      <span
        className="ml-2 mt-0 col-md-8 px-0"
        style={{
          fontSize: "16px",
          fontWeight: 400,
          color:
            localStorage.getItem("mantine-color-scheme") === "dark"
              ? "#d5d7e0"
              : "#475f7b",
        }}
      >
        &nbsp;{t("grid.header.co2AndExchange")}
      </span>
    </>
  );

  const children = () => (
    <div
      data-id="accordion-grid-1"
      onClick={handleContainerClick}
      onKeyDown={handleContainerKeyDown}
      ref={containerRef}
      role="button"
      tabIndex={0}
    >
      <Co2EmissionFixed
        form={form}
        handleInputChange={handleInputChange}
        gridParameters={gridParameters}
        getFieldAttributes={getFieldAttributes}
      />
       <Text component="span" fw={700}>
        {/* {t("grid.text.exportAndDemandOptimization")} */}
        {t("grid.text.configurePowerImport")}
      </Text>
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col {...responsiveCol}>
          <Select
            {...getFieldAttributes("field-9")}
            data-id="field-9"
            data-testid="power-import"
            title="Power Import"
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("grid.label.powerImportLimit")}
            data={[
              
              { value: "Unlimited Import", label: "No Limit" },
              { value: "On Purchase Limit", label: "On Import Limit" },
            ]}
            withAsterisk
            placeholder={t("common.selectOption")}
            value={
              gridParameters?.componentAttributes?.gridParameters?.powerImport
                ?.condition
            }
            {...form.getInputProps(
              "componentAttributes.gridParameters.powerImport.condition"
            )}
            onChange={(value) => {
              handleInputChange(
                "componentAttributes",
                "powerImport.condition",
                value
              );
            }}
            withinPortal
          />
        </Grid.Col>
        {(gridParameters?.componentAttributes?.gridParameters?.powerImport
          ?.condition === "On Purchase Limit")&& (
          <Grid.Col {...responsiveCol}>
            <TextInput
            {...getFieldAttributes("field-10")}
             data-id="field-10"
              label={t("grid.label.purchaseLimitPercent")}
              type="number"
              placeholder={t("grid.placeholder.addPurchaseLimit")}
              value={
                gridParameters?.componentAttributes?.gridParameters?.powerImport
                  ?.value
              }
              {...form.getInputProps(
                "componentAttributes.gridParameters.powerImport.value"
              )}
              onChange={(event) => {
                handleInputChange(
                  "componentAttributes",
                  "powerImport.value",
                  event.target.value
                );
              }}
              required
              rightSection={
                <div style={grayIconStyle}>kW</div>
              }
              rightSectionProps={{ style: { pointerEvents: "none" } }}
            />
          </Grid.Col>
        )}
 </Grid>
      {/* <Text component="span" fw={700}>
        {t("grid.text.configurePowerExport")}
      </Text> */}
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col {...responsiveCol}>
          <Select
            {...getFieldAttributes("field-0")}
            data-id="field-0"
            data-testid="power-export"
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("grid.label.powerExportLimit")}
            data={[
              
              { value: "No Export", label: "No Export" },
              { value: "Unlimited Export", label: "Unlimited Export" },
              { value: "On Purchase Limit", label: "On Export Limit" },
            ]}
            withAsterisk
            placeholder={t("common.selectOption")}
            value={
              gridParameters?.componentAttributes?.gridParameters?.powerExport
                ?.condition
            }
            {...form.getInputProps(
              "componentAttributes.gridParameters.powerExport.condition"
            )}
            onChange={(value) => {
              handleInputChange(
                "componentAttributes",
                "powerExport.condition",
                value
              );
            }}
            withinPortal
          />
        </Grid.Col>
        {(gridParameters?.componentAttributes?.gridParameters?.powerExport
          ?.condition === "On Purchase Limit")&& (
          <Grid.Col {...responsiveCol}>
            <TextInput
            {...getFieldAttributes("field-11")}
             data-id="field-11"
              label={t("grid.label.purchaseLimitPercent")}
              type="number"
              placeholder={t("grid.placeholder.addPurchaseLimit")}
              value={
                gridParameters?.componentAttributes?.gridParameters?.powerExport
                  ?.percentage
              }
              {...form.getInputProps(
                "componentAttributes.gridParameters.powerExport.percentage"
              )}
              onChange={(event) => {
                handleInputChange(
                  "componentAttributes",
                  "powerExport.percentage",
                  event.target.value
                );
              }}
              required
              rightSection={
                <div style={grayIconStyle}>kW</div>
              }
              rightSectionProps={{ style: { pointerEvents: "none" } }}
            />
          </Grid.Col>
        )}
 </Grid>
 <Text component="span" fw={700}>
        {/* {t("grid.text.exportAndDemandOptimization")} */}
        {t("grid.text.configureDemandOptimization")}
      </Text>
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col {...responsiveCol}>
          <Select
            {...getFieldAttributes("field-5")}
            data-id="field-5"
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("grid.label.demandManagement")}
            data={[
              { value: "none", label: "None" },
              { value: "full", label: "Full" },
            ]}
            value={
              gridParameters?.uiAttributes?.gridParameters?.demandManagement
                ?.type
            }
            placeholder={t("common.selectOption")}
            {...form.getInputProps(
              "uiAttributes.gridParameters.demandManagement.type"
            )}
            onChange={(value) => {
              handleInputChange("uiAttributes", "demandManagement.type", value);
            }}
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <TextInput
            {...getFieldAttributes("field-6")}
            data-id="field-6"
            required={
              gridParameters?.uiAttributes?.gridParameters?.demandManagement
                ?.type === "full"
            }
            label={t("grid.label.demandReset")}
            type="number"
            placeholder={t("grid.placeholder.addDemandResetValue")}
            step="0.1"
            {...form.getInputProps("uiAttributes.gridParameters.demandReset")}
            onChange={(event) => {
              handleInputChange(
                "uiAttributes",
                "demandReset",
                event.target.value
              );
            }}
            rightSection={
              <div
                style={grayIconStyle}
              >
                <IconPercentage size={18} />
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        {gridParameters?.uiAttributes?.gridParameters?.demandManagement
          ?.type === "limited" && (
          <Grid.Col {...responsiveCol}>
            <TextInput
              label=" "
              type="text"
              className="mt-2"
              placeholder={t("grid.placeholder.addValue0to9")}
              value={
                gridParameters?.uiAttributes?.gridParameters?.demandManagement
                  ?.value || ""
              }
              {...form.getInputProps(
                "uiAttributes.gridParameters.demandManagement.value"
              )}
              onChange={(event) => {
                handleInputChange(
                  "uiAttributes",
                  "demandManagement.value",
                  event.target.value
                );
              }}
            />
          </Grid.Col>
        )}
     </Grid>
      <Text component="span" fw={700}>
        {t("grid.text.configureTimeOfUse")}
      </Text>
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col {...responsiveCol}>
          <Checkbox
            {...getFieldAttributes("field-24")}
            data-id="field-24"
            label={t("grid.label.timeOptimization")}
            checked={
              gridParameters?.uiAttributes?.gridParameters
                ?.timeOfUseOptimization
            }
            {...form.getInputProps(
              "uiAttributes.gridParameters.timeOfUseOptimization"
            )}
            onChange={(event) => {
              handleInputChange(
                "uiAttributes",
                "timeOfUseOptimization",
                event.currentTarget.checked
              );
            }}
          />
        </Grid.Col>
      </Grid>
      {gridParameters?.uiAttributes?.gridParameters?.optimizeDemandResponse && (
        <Grid gutter="md" className="mt-2 mb-2">
          <Grid.Col {...responsiveCol}>
            <FileInput
              label={t("grid.label.uploadLoadFile")}
              placeholder={t("common.chooseFile")}
              onChange={(file) => {
                console.log(
                  "please add handler for upload file in gridParameters"
                );
              }}
              rightSection={
                <div style={grayIconStyle}>
                  <IconFileSearch size={18} />
                </div>
              }
              rightSectionProps={{ style: { pointerEvents: "none" } }}
            />
          </Grid.Col>
        </Grid>
      )}
      {/* <Grid>
        <Grid.Col style={{ alignContent: "flex-end" }} {...responsiveCol}>
          <Switch
            labelPosition="left"
            label={t("grid.switch.investmentEnable")}
            checked={
              gridParameters?.uiAttributes?.gridParameters
                ?.operationCapitalCost || false
            }
            color="seGreen.0"
            {...form.getInputProps(
              "uiAttributes.gridParameters.operationCapitalCost"
            )}
            onChange={(event) => {
              handleInputChange(
                "uiAttributes",
                "operationCapitalCost",
                event.currentTarget.checked
              );
            }}
          />
        </Grid.Col>
        <Grid.Col style={{ alignContent: "flex-end" }} {...responsiveCol}>
          <Switch
            color="seGreen.0"
            labelPosition="left"
            label={t("grid.switch.enableOutageAndLoadManagement")}
            checked={
              gridParameters?.uiAttributes?.gridParameters
                ?.addOutageManageLoad || false
            }
            {...form.getInputProps(
              "uiAttributes.gridParameters.addOutageManageLoad"
            )}
            onChange={(event) => {
              handleInputChange(
                "uiAttributes",
                "addOutageManageLoad",
                event.currentTarget.checked
              );
            }}
          />
        </Grid.Col>
      </Grid> */}
    </div>
  );
  return (
    <AccordionItem
      id={1}
      header={header()}
      key={1}
      children={children()}
      height="100%"
    />
  );
};

GridPowerExchange.propTypes = {
  form: PropTypes.object,
  handleInputChange: PropTypes.func,
  gridParameters: PropTypes.object,
  getFieldAttributes: PropTypes.func,
};

export default GridPowerExchange;
